import { motion } from 'framer-motion'
import { Clock, MapPin, Calendar, Sun, BookOpen, Flame, Users, ArrowRight } from 'lucide-react'
import { useSite } from '../../context/SiteContext'
import { Link } from 'react-router-dom'

const serviceIcons = {
  'Devotional Worship': { icon: Sun, color: 'from-amber-400 to-orange-500', bg: 'bg-amber-50' },
  'Bible Study': { icon: BookOpen, color: 'from-emerald-400 to-teal-500', bg: 'bg-emerald-50' },
  'Revival Hour': { icon: Flame, color: 'from-red-400 to-rose-500', bg: 'bg-red-50' },
  'Small Group Meetings': { icon: Users, color: 'from-violet-400 to-purple-500', bg: 'bg-violet-50' },
}

const ServiceCard = ({ service, index }) => {
  const config = serviceIcons[service.name] || { icon: Clock, color: 'from-primary-400 to-primary-600', bg: 'bg-primary-50' }
  const Icon = config.icon

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ y: -8, scale: 1.02 }}
      className="group relative bg-white rounded-3xl p-6 shadow-soft hover:shadow-soft-lg transition-all duration-500 cursor-pointer overflow-hidden"
    >
      {/* Gradient Border Effect */}
      <div className={`absolute inset-0 bg-gradient-to-br ${config.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl`} />
      <div className="absolute inset-[2px] bg-white rounded-[22px]" />

      {/* Content */}
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div className={`p-3 rounded-2xl ${config.bg} group-hover:scale-110 transition-transform duration-300`}>
            <Icon className={`w-6 h-6 bg-gradient-to-br ${config.color} bg-clip-text text-transparent`} style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
            <Icon className={`w-6 h-6 absolute top-3 left-3 opacity-100`} style={{ color: config.color.includes('amber') ? '#f59e0b' : config.color.includes('emerald') ? '#10b981' : config.color.includes('red') ? '#ef4444' : '#8b5cf6' }} />
          </div>
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-light-100 text-dark-300">
            {service.day}
          </span>
        </div>

        <h3 className="text-xl font-heading font-bold text-dark-100 mb-2 group-hover:text-primary-600 transition-colors">
          {service.name}
        </h3>

        <div className="flex items-center gap-2 text-dark-300">
          <Clock className="w-4 h-4" />
          <span className="font-accent font-semibold text-lg">{service.time}</span>
        </div>
      </div>
    </motion.div>
  )
}

const ServiceTimes = () => {
  const { settings, getNextService, getGoogleMapsUrl } = useSite()
  const nextService = getNextService()

  return (
    <section className="py-24 bg-gradient-to-b from-light to-white relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-primary-50/50 to-transparent" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary-100/30 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-semibold mb-4">
            <Clock className="w-4 h-4" />
            Join Us
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark-100 mb-4">
            Service <span className="gradient-text">Times</span>
          </h2>
          <p className="text-lg text-dark-300 max-w-2xl mx-auto">
            We'd love to have you worship with us! Check out our weekly services and find one that fits your schedule.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 items-start">
          {/* Service Cards */}
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
            {settings.serviceTimes?.map((service, index) => (
              <ServiceCard key={index} service={service} index={index} />
            ))}
          </div>

          {/* Next Service Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="sticky top-24">
              <div className="bg-gradient-to-br from-primary-500 via-secondary-500 to-accent-500 rounded-3xl p-1 shadow-xl shadow-primary-500/20">
                <div className="bg-white rounded-[22px] p-8">
                  {/* Animated Badge */}
                  <div className="flex items-center gap-2 mb-6">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-accent-500"></span>
                    </span>
                    <span className="text-sm font-semibold text-dark-300">Next Service</span>
                  </div>

                  <h3 className="text-2xl font-heading font-bold text-dark-100 mb-6">
                    {nextService?.name}
                  </h3>

                  <div className="space-y-4 mb-8">
                    <div className="flex items-center gap-4 p-4 rounded-2xl bg-light-100">
                      <div className="p-2 rounded-xl bg-primary-100">
                        <Calendar className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="text-sm text-dark-300">When</p>
                        <p className="font-semibold text-dark-100">{nextService?.day} at {nextService?.time}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 p-4 rounded-2xl bg-light-100">
                      <div className="p-2 rounded-xl bg-secondary-100">
                        <MapPin className="w-5 h-5 text-secondary-600" />
                      </div>
                      <div>
                        <p className="text-sm text-dark-300">Where</p>
                        <p className="font-semibold text-dark-100">{settings.address?.street}</p>
                      </div>
                    </div>
                  </div>

                  {nextService?.daysUntil !== undefined && (
                    <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-2xl p-6 text-center mb-6">
                      <span className="text-sm text-dark-300 font-medium">Coming up in</span>
                      <div className="text-4xl font-heading font-bold gradient-text mt-2">
                        {nextService.daysUntil === 0 ? 'Today! 🎉' : `${nextService.daysUntil} day${nextService.daysUntil !== 1 ? 's' : ''}`}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <a
                      href={getGoogleMapsUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border-2 border-primary-200 text-primary-600 font-semibold hover:bg-primary-50 transition-colors"
                    >
                      <MapPin className="w-4 h-4" />
                      Directions
                    </a>
                    <Link
                      to="/plan-your-visit"
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold shadow-lg shadow-primary-500/25 hover:shadow-xl transition-all"
                    >
                      Visit
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default ServiceTimes
